import { ninetyDeg } from '@baseValues'
import { getGLBData } from '@proxies/getGLBData'
import { setResource, useAppStore } from '@store'
import { Getter, LightType, Setter } from '@types'
import toQuaternion from '@utils/conversion/toQuaternion'
import { toVector3 } from '@utils/conversion/toVector3'
import { radians } from '@utils/generalMaths/geometry'
import getId from '@utils/sceneObjects/getId'
import { getGLB } from '@utils/sceneObjects/items/getGLB'
import { getLightData } from '@utils/sceneObjects/lights/getLightData'
import { setGLTFLoader } from '@utils/sceneObjects/setGLTFLoader'
import * as THREE from 'three'
import { Matrix4 } from 'three'

export const createLightActions = (set: Setter, get: Getter) => ({
  drawLight: async (
    id: string,
    icono?: string,
    catalogID: string,
    show = true,
    height?: number,
    matrix?: Matrix4,
  ) => {
    const glbInScene = get().sceneGLB
    const glbData = await getGLB(glbInScene, catalogID)
    if (!glbData) return
    const { idUnidades, descripcion, tipoNormalizado, referenciaBlender, altura } = glbData!
    const {
      idUnidadesLuces,
      angle,
      castShadow,
      color,
      distance,
      intensity,
      penumbra,
      power,
      tipoLuzDescripcion,
    } = glbData.unidadesLuces[0]

    const lightType = getLightData(tipoNormalizado)
    const posicion = toVector3([0, 0, 0])
    const altopared = height ? height : altura || altura === 0 ? altura : get().prefs.ALTOPARED

    const glbAltura = altopared + glbData.alturaColocacion || 0
    if (matrix) {
      matrix.decompose(posicion, toQuaternion(), toVector3())
      //para copiar items en L rotarla
      // y separar tambien en el otro eje el thickness de la pieza
      //matrix.makeRotationY(-ninetyDeg)
    }

    const textures = {
      angle: { value: angle, name: 'Ángulo', min: 0, max: 180, step: 1 },
      distance: { value: distance, name: 'Distancia', min: 0, max: Infinity, step: 1 },
      penumbra: { value: penumbra, name: 'Penumbra', min: 0, max: 10, step: 0.1 },
      power: { value: power, name: 'Potencia', min: 0, max: 1000, step: 1 },
      castShadow: { value: castShadow, name: 'Mostrar sombras' },
      intensity: intensity,
    }

    if (glbInScene.has(catalogID)) {
      const glbData = useAppStore.getState().sceneGLB.get(catalogID)
      if (!glbData) {
        return
      }
      const { glb } = glbData
      const item = {
        id,
        idUnidadesLuces,
        tipoNormalizado,
        serverID: idUnidades,
        description: descripcion,
        description2: tipoLuzDescripcion,
        intensity: intensity,
        glbPath: referenciaBlender,
        color,
        catalogID,
        textures,
        type: 'lights',
        lightType,
        selected: true,
        visible: true,
        image: icono,
        matrix: matrix
          ? new Matrix4().copy(matrix).setPosition(posicion.x, glbAltura, posicion.z)
          : (() => {
              const initialMatrix = new Matrix4()
              // Para spotlights, rotación inicial de 270 grados
              if (lightType === 'spotlight') {
                initialMatrix.makeRotationY(radians(270))
              } else {
                initialMatrix.makeRotationY(-ninetyDeg)
              }
              initialMatrix.setPosition(toVector3([posicion.x ?? 0, glbAltura ?? 3999, posicion.z ?? 0]))
              return initialMatrix
            })(),
        x: posicion.x ?? 0,
        y: glbAltura ?? 3999,
        z: posicion.z ?? 0,
        width: glbData.ancho,
        rotation: lightType === 'spotlight' ? 270 : 0,
      } satisfies LightType

      set((store) => {
        store.items.set(id, item)
        store.layers[store.currentLayer].items.add(id)
        show && store.layers[store.currentLayer].selected.add(id)
      })
    } else {
      const loader = await setGLTFLoader()

      loader.load(referenciaBlender, (glb) => {
        const item = {
          id,
          idUnidadesLuces,
          tipoNormalizado,
          serverID: idUnidades,
          description: descripcion,
          description2: tipoLuzDescripcion,
          glbPath: referenciaBlender,
          intensity,
          color,
          textures,
          width: glbData.ancho,
          type: 'lights',
          lightType,
          catalogID,
          selected: true,
          visible: true,
          image: icono,
          matrix: matrix
            ? new Matrix4().copy(matrix).setPosition(posicion.x, glbAltura, posicion.z)
            : new Matrix4()
                .makeRotationY(-ninetyDeg)
                .setPosition(toVector3([posicion.x ?? 0, glbAltura ?? 3999, posicion.z ?? 0])),
          x: posicion.x,
          y: glbAltura ?? 3999,
          z: posicion.z,
          rotation: 0,
          misc: {},
        } satisfies LightType

        set((store) => {
          store.sceneGLB.set(catalogID, { glb, glbData })
          setResource('light', true)
          store.items.set(id, item)
          store.layers[store.currentLayer].items.add(id)
          show && store.layers[store.currentLayer].selected.add(id)
        })
      })
    }
  },
  drawLightSun: async (
    id: string,
    icono?: string,
    catalogID: string,
    show = false,
    height?: number,
    matrix?: Matrix4,
  ) => {
    const glbInScene = get().sceneGLB
    const glbData = await getGLB(glbInScene, catalogID)
    if (!glbData) return
    const { idUnidades, descripcion, tipoNormalizado, referenciaBlender, altura } = glbData!
    const {
      idUnidadesLuces,
      angle,
      castShadow,
      color,
      distance,
      intensity,
      penumbra,
      power,
      tipoLuzDescripcion,
    } = glbData.unidadesLuces[0]

    const lightType = getLightData(tipoNormalizado)

    const initialPosition = { x: 10000, y: 3999, z: 10000 }

    const posicion = toVector3([initialPosition.x, initialPosition.y, initialPosition.z])
    const altopared = height ? height : altura || altura === 0 ? altura : get().prefs.ALTOPARED

    const glbAltura = altopared + glbData.alturaColocacion || 0
    if (matrix) {
      matrix.decompose(posicion, toQuaternion(), toVector3())
    }

    const textures = {
      angle: { value: angle, name: 'Ángulo', min: 0, max: 180, step: 1 },
      distance: { value: distance, name: 'Distancia', min: 0, max: Infinity, step: 1 },
      penumbra: { value: penumbra, name: 'Penumbra', min: 0, max: 10, step: 0.1 },
      power: { value: power, name: 'Potencia', min: 0, max: 1000, step: 1 },
      castShadow: { value: castShadow, name: 'Mostrar sombras' },
      intensity: intensity,
    }

    if (glbInScene.has(catalogID)) {
      const { glb } = useAppStore.getState().sceneGLB.get(catalogID)
      const item = {
        id,
        idUnidadesLuces,
        tipoNormalizado,
        serverID: idUnidades,
        description: descripcion,
        description2: tipoLuzDescripcion,
        intensity: intensity,
        glbPath: referenciaBlender,
        color,
        catalogID,
        textures,
        type: 'lights',
        lightType,
        selected: true,
        visible: true,
        image: icono,
        matrix: matrix
          ? new Matrix4().copy(matrix).setPosition(posicion.x, glbAltura, posicion.z)
          : new Matrix4()
              .makeRotationY(-ninetyDeg)
              .setPosition(toVector3([initialPosition.x, glbAltura, initialPosition.z])),
        x: initialPosition.x,
        y: glbAltura,
        z: initialPosition.z,
        width: glbData.ancho,
        rotation: 0,
      } satisfies LightType

      set((store) => {
        store.items.set(id, item)
        store.layers[store.currentLayer].items.add(id)
        show && store.layers[store.currentLayer].selected.add(id)
      })
    } else {
      const loader = await setGLTFLoader()

      loader.load(referenciaBlender, (glb) => {
        const item = {
          id,
          idUnidadesLuces,
          tipoNormalizado,
          serverID: idUnidades,
          description: descripcion,
          description2: tipoLuzDescripcion,
          glbPath: referenciaBlender,
          intensity,
          color,
          textures,
          width: glbData.ancho,
          type: 'lights',
          lightType,
          catalogID,
          selected: true,
          visible: true,
          image: icono,
          matrix: matrix
            ? new Matrix4().copy(matrix).setPosition(posicion.x, glbAltura, posicion.z)
            : new Matrix4()
                .makeRotationY(-ninetyDeg)
                .setPosition(toVector3([initialPosition.x, glbAltura, initialPosition.z])),
          x: initialPosition.x,
          y: glbAltura,
          z: initialPosition.z,
          rotation: 0,
          misc: {},
        } satisfies LightType

        set((store) => {
          store.sceneGLB.set(catalogID, { glb, glbData })
          setResource('light', true)
          store.items.set(id, item)
          store.layers[store.currentLayer].items.add(id)
          show && store.layers[store.currentLayer].selected.add(id)
        })
      })
    }
  },
  editLight: <T extends LightType>(id: string, config?: Partial<Record<keyof T, T[keyof T]>>) => {
    if (config) {
      set((store) => {
        const item = store.items.get(id)
        if (!item) return
        for (const [property, value] of Object.entries(config)) {
          item[property] = value
        }
      })
    }
  },
  editLightProperty: <T extends LightType>(
    id: string,
    config?: Partial<Record<keyof T, T[keyof T]>>,
  ) => {
    if (config) {
      set((store) => {
        for (const [property, value] of Object.entries(config)) {
          const item = store.items.get(id)
          if (!item) continue
          item.textures[property].value = value
        }
      })
    }
  },
  removeNonBaseLights: () => {
    set((store) => {
      for (const [id, item] of store.items) {
        if (
          item.type === 'lights' &&
          id !== 'lights_baseSceneLight' &&
          id !== 'lights_baseAreaLight'
        ) {
          store.items.delete(id)
          // Opcional: eliminar de layers si es necesario
          for (const layer of store.layers) {
            layer.items.delete(id)
            layer.selected.delete(id)
          }
        }
      }
    })
  },
  removedLights: new Map(),
  toggleNonBaseLights: () => {
    set((store) => {
      const projectName = store.clientInfo.nombreProyecto
      const removedLights = store.removedLightsByProject.get(projectName)

      if (removedLights) {
        // Restore lights
        removedLights.forEach((light, id) => {
          store.items.set(id, light)
          store.layers[store.currentLayer].items.add(id)
        })
        store.removedLightsByProject.delete(projectName)
      } else {
        // Save and remove lights
        const lightsToRemove = new Map()
        store.items.forEach((item, id) => {
          if (item.type === 'lights' && !id.startsWith('lights_base')) {
            lightsToRemove.set(id, item)
            store.items.delete(id)
            store.layers[store.currentLayer].items.delete(id)
          }
        })
        if (lightsToRemove.size > 0) {
          store.removedLightsByProject.set(projectName, lightsToRemove)
        }
      }
    })
  },
  restoreAllLights: () => {
    set((store) => {
      const projectName = store.clientInfo.nombreProyecto
      const removedLights = store.removedLightsByProject.get(projectName)

      if (removedLights) {
        removedLights.forEach((light, id) => {
          store.items.set(id, light)
          store.layers[store.currentLayer].items.add(id)
        })
        store.removedLightsByProject.delete(projectName)
      }
    })
  },
  fetchAndAddLight: async (catalogID: string | number) => {
    const glbData = await getGLBData(catalogID)

    if (!glbData) {
      console.warn('Light data not found.')
      return
    }

    const { idUnidades, descripcion, referenciaBlender, altura, unidadesLuces } = glbData

    const lightData = unidadesLuces?.[0]
    if (!lightData) {
      console.warn('Light unit data not found.')
      return
    }

    const id = getId('lights')

    const lightItem = {
      id,
      serverID: idUnidades,
      description: descripcion,
      type: 'lights',
      lightType: 'directionallight',
      glbPath: referenciaBlender,
      position: { x: 0, y: altura ?? 0, z: 0 },
      intensity: lightData.intensity ?? 1,
      color: lightData.color ?? '#FFFFFF',
    }

    // Agregar la luz al estado
    set((state) => {
      const items = new Map(state.items)
      items.set(id, lightItem)
      return { items }
    })

    // Dibujar la luz en la escena
    const scene = get().scene
    const light = new THREE.DirectionalLight(lightData.color, lightData.intensity)
    light.position.set(0, altura ?? 0, 0)
    light.name = id
    scene.add(light)
  },
  updateLightHorizontal: (id: string, x: number, z: number) => {
    // Validar que las coordenadas no sean NaN
    if (isNaN(x) || isNaN(z)) {
      console.error(`Coordenadas inválidas para luz ${id}:`, { x, z })
      return
    }
    
    set((store) => {
      const item = store.items.get(id)
      if (!item) return

      // Actualiza las coordenadas en el objeto
      item.matrix = item.matrix.clone().setPosition(x, item.y, z)
      item.x = x
      item.z = z
    })
  },
  updateLightVertical: (id: string, y: number) => {
    // Validar que la coordenada no sea NaN
    if (isNaN(y)) {
      console.error(`Coordenada Y inválida para luz ${id}:`, { y })
      return
    }
    
    set((store) => {
      const item = store.items.get(id)
      if (!item) return

      // Actualiza la coordenada 'y' en el objeto
      item.matrix = item.matrix.clone().setPosition(item.x, y, item.z) // Mantén x y z
      item.y = y // Actualiza solo 'y'
    })
  },
})
